import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  ConnectionEndPointListRequest,
  useFetchConnectionEndPointsQuery,
} from '../connectionendpoint-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MinusCircleFilled,
  MoreOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { ConnectionEndPointViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function ConnectionEndPointsScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'host': 'Host',
      'description': 'Description',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const connectionendpointTable: TableState = {
    columns: [
      {
        title: 'Connection EndPoint',
        dataIndex: '',
        key: 'Id',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
              <Space>
                <span>{connectionendpoint.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Host',
        dataIndex: '',
        key: 'host',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
          <Tooltip title={connectionendpoint.host} >
            <span>{connectionendpoint.host}</span>
            </Tooltip>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('host'),
      },
      {
        title: 'Service Type',
        dataIndex: '',
        key: 'serviceType',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <><Tooltip title={connectionendpoint.serviceType} >
          <span>{connectionendpoint.serviceType}</span>
          </Tooltip>             
          </>
        ),
      },
      {
        title: 'Protocol',
        key: 'protocol',
        dataIndex: '',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
              <span>{connectionendpoint.protocol}</span>
          </>
        ),
      },   


      {
        title: 'ServiceName',
        dataIndex: '',
        key: 'serviceName',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
          <Tooltip title={connectionendpoint.serviceName} >
            <span>{connectionendpoint.serviceName}</span>
            </Tooltip>
          </>
        ),
      },
      {
        title: 'ProcessID',
        dataIndex: '',
        key: 'processID',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <><Tooltip title={connectionendpoint.processID} >
          <span>{connectionendpoint.processID}</span>
          </Tooltip>             
          </>
        ),
      },
      {
        title: 'EnvironmentKey',
        key: 'environmentKey',
        dataIndex: '',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
              <span>{connectionendpoint.environmentKey}</span>
          </>
        ),
      },   
      {
        title: 'Description',
        dataIndex: '',
        key: 'description',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
          <Tooltip title={connectionendpoint.description} >
            <span>{connectionendpoint.description}</span>
            </Tooltip>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('description'),
      },
      {
        title: 'IsActive',
        key: 'isActive',
        dataIndex: '',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <>
              <span>{connectionendpoint.isActive? 'True' : 'False' }</span>
          </>
        ),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (connectionendpoint: ConnectionEndPointViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('ConnectionEndPointEdit', {
                      tenantId: params.tenantId,
                      Id: connectionendpoint.id,
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="delete"
                  disabled={!!(deletingId && deletingId === connectionendpoint.id)}
                  onClick={() => deleteEntity(connectionendpoint.id ?? 0, connectionendpoint.protocol ?? '')}
                >
                  {deletingId === connectionendpoint.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): ConnectionEndPointListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<ConnectionEndPointListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<ConnectionEndPointViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchConnectionEndPointsQuery(pagination, { pollingInterval: DefaultPollingInterval });

  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'ConnectionEndPoints',
    tagType: 'ConnectionEndPoint',
    entityLabel: 'connection endpoint',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('ConnectionEndPointEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ConnectionEndPointViewModel> | SorterResult<ConnectionEndPointViewModel>[],
    extra: TableCurrentDataSource<ConnectionEndPointViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<ConnectionEndPointViewModel>;

    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;

    // Check if we're actually changing sort or just pagination
    const isSortingAction = columnKey && order;
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort;
    
    if (isSortingAction) {
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'host': 'Host',
        'description': 'Description',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort
      newSortBy = currentSortBy || defaultSort;
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    const newUrl = getLink(
      'ConnectionEndPointList',
      { ...params },
      {
        sortBy: newSortBy,
        p: newPage,
        size: paginationConfig.pageSize,
        q: urlParams.get('q') || '',
      }
    );
    
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    let finalSearch = searchText;
    if(searchText===':') {
      message.error(
      `Please enter valid url text.`
    );
    return;}
    else if(searchText.includes(':/') || searchText.includes('://')){
      finalSearch = searchText.split(':')[1];
    }
    navigate(
      getLink(
        'ConnectionEndPointList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q: (finalSearch?.length>0 ? `Host:*${finalSearch.replaceAll(" ", "\\ ")}*` : ''),
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };


  return (
    <div>
      <PageHeader
        extra={
          <Button htmlType="button" data-testid={'create-new-connectionendpoint'} onClick={onCreateNew}>
            New Connection Endpoint
          </Button>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Connection Endpoints
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="connectionendpoints">
              <Link to={getLink('ConnectionEndPointList', { tenantId: params.tenantId })}>Connection Endpoints</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Input.Search
              placeholder="Search by Host"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
            />
            <Table
            scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={connectionendpointTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'ConnectionEndpoint'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
